import React, {useState, useEffect, useCallback} from 'react';
import {getConnectionStatus} from '../services/socketService';
import {Form, Input, Button, Row, Col, Space, Typography, Modal, Alert, Tooltip, Select} from 'antd';
import {useMediaQuery} from 'react-responsive';
import {motion} from 'framer-motion';
import {useSelector} from 'react-redux';
import {CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import promoterService from '../services/PromoterService';
import mermaidValidationService from '../services/MermaidValidationService';
import {store} from '../store/store';
import theme from "../theme";
import zipCodeRangeService from "../services/ZipCodeRangeService";
import electorKeyValidator from "../services/ElectorKeyValidator";
import IneValidator from '../services/IneValidator';
import {addPendingPromoterForm} from "../store/reducers/formPromoterReducer";
import goalService from "../services/GoalService";
import {useNavigate} from 'react-router-dom';
import {PROMOTER_REGISTRATION_ENABLED} from '../utils/featureFlags';
import {useUserSections} from '../utils/userSessionUtils';
import {temporaryEmailRule} from '../utils/emailValidator';

const {Title, Text} = Typography;
const {Option} = Select;

const PromoterRegisterPage = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [limitReached, setLimitReached] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalMessage, setModalMessage] = useState('');
    const [connectionStatus, setConnectionStatus] = useState(getConnectionStatus());
    const [zipCodeRange, setZipCodeRange] = useState({});
    const [areaCodes, setAreaCodes] = useState([]);
    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const entityId = auth.user?.entityId || 0;
    const municipalityId = auth.user?.municipalityId || 0;
    const districtId = auth.user?.districtId || 0;
    const districtLocal = auth.user?.districtLocal || null;
    const section = useSelector((state) => state.section);
    const [modal, contextHolder] = Modal.useModal();
    const navigate = useNavigate();
    const userSections = useUserSections();
    const [selectedSection, setSelectedSection] = useState(null);

    useEffect(() => {
        fetch('/areaCodes.json')
            .then(res => res.json())
            .then(data => setAreaCodes(data))
            .catch(err => console.error('Error loading area codes:', err));
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setConnectionStatus(getConnectionStatus());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchZipCodeRange = async () => {
            try {
                const filters = {
                    entityId: {value: entityId, operator: "eq"},
                    municipalityId: {value: municipalityId, operator: "eq"},
                };
                const responseZipCodeRange = await zipCodeRangeService.getAll(filters);
                if (responseZipCodeRange.data.length > 0) {
                    setZipCodeRange(responseZipCodeRange.data[0]);
                }
            } catch (error) {
                console.error("Error fetching zip code data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchZipCodeRange().catch(console.error);
    }, [entityId, municipalityId]);

    useEffect(() => {
        const checkPromoterLimit = async () => {
            if (connectionStatus && userSections.length > 0) {
                try {
                    // Set the selected section (use first section if only one, otherwise will need section selector)
                    if (userSections.length === 1) {
                        setSelectedSection(userSections[0]);
                    }

                    const filters = {
                        entityId: {value: entityId, operator: "eq"},
                        sectionId: {value: userSections, operator: "in"},
                    };

                    const {data: goals} = await goalService.getAll(filters);
                    const {meta: metaPromoters} = await promoterService.getAll({
                        ...filters,
                        status: {value: "ACTIVE", operator: "eq"}
                    });

                    // if (goals.length === 1 && goals[0].promotersLimit <= metaPromoters.totalItems) {
                    //     setLimitReached(true);
                    // }
                } catch (error) {
                    console.error("Error checking promoter limit:", error);
                }
            }
        };

        checkPromoterLimit();
    }, [connectionStatus, userSections, entityId, districtId, modal, navigate]);

    useEffect(() => {
        if (selectedSection) {
            form.setFieldsValue({
                sectionId: selectedSection
            });
        }
    }, [selectedSection, form]);

    const handleSubmit = useCallback(async (values) => {
        setLoading(true);

        const formData = {
            ...values,
            phone: values.phone.replace(/\D/g, ''),
            entityId,
            municipalityId,
            districtId,
            districtLocal,
            sectionId: parseInt(values.sectionId.toString()),
            interiorNumber: !values.interiorNumber || values.interiorNumber.trim() === '' ? null : values.interiorNumber,
        };

        if (getConnectionStatus()) {
            try {
                // Validate CURP and INE are different
                if (formData.curp === formData.electorKey) {
                    showModal('error', 'El CURP y la Clave de Elector no pueden ser iguales.');
                    setLoading(false);
                    return;
                }

                // Validate INE in Mermaid first
                const validationINE = await mermaidValidationService.validateUserInMermaid(formData.electorKey);
                if (validationINE.exists) {
                    const roles = validationINE.rol && validationINE.rol.length > 0 
                        ? ` con rol: ${validationINE.rol.join(', ')}` 
                        : '';
                    showModal('error', `No se puede registrar el promotor, la persona ya está en el sistema${roles}.`);
                    setLoading(false);
                    return;
                }

                // Validate CURP in Mermaid
                const validationCURP = await mermaidValidationService.validateUserInMermaid(formData.curp);
                if (validationCURP.exists) {
                    const roles = validationCURP.rol && validationCURP.rol.length > 0 
                        ? ` con rol: ${validationCURP.rol.join(', ')}` 
                        : '';
                    showModal('error', `No se puede registrar el promotor, la persona ya está en el sistema${roles}.`);
                    setLoading(false);
                    return;
                }

                const filters = {
                    entityId: { value: formData.entityId, operator: "eq" },
                    sectionId: { value: formData.sectionId, operator: "in" },
                };
                // const { data: goals } = await goalService.getAll(filters);
                // const { meta: metaPromoters } = await promoterService.getAll({
                //     ...filters,
                //     status: { value: "ACTIVE", operator: "eq" }
                // });
                // if (goals.length === 1 && goals[0].promotersLimit > metaPromoters.totalItems) {
                    await promoterService.create(formData);
                    form.resetFields();
                    showModal('success', 'Registro de promotor exitoso.');
                // } else {
                //     modal.warning({
                //         title: 'Límite de promotores',
                //         content: 'No está permitido agregar más promotores',
                //     });
                // }
            } catch (error) {
                if (error.errorCode === "VALIDATION_ERROR" || error.errorCode === "BAD_REQUEST") {
                    const validationErrors = error.details;
                    const errorMessages = validationErrors.flatMap(errorObj => Object.values(errorObj));
                    showModal('error', (
                        <>
                            <p><b>Por favor corrige los siguientes errores:</b></p>
                            <ul style={{textAlign: "left"}}>
                                {errorMessages.map((msg, index) => (
                                    <li key={index}>{msg}</li>
                                ))}
                            </ul>
                        </>
                    ));
                } else {
                    showModal('error', error.message || 'Error al enviar el formulario.');
                }
            }
        } else {
            form.resetFields();
            formData['user'] = auth.user.username;
            store.dispatch(addPendingPromoterForm(formData));
            showModal('offline', 'El formulario será enviado cuando haya conexión.');
        }

        setLoading(false);
    }, [entityId, municipalityId, districtId, districtLocal, form, modal, auth.user.username]);

    const showModal = (type, message) => {
        setModalType(type);
        setModalMessage(message);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        if (modalType === 'success') {
            navigate('/promotores');
        }
    };

    const validateIneMatch = () => {
        const electorKey = form.getFieldValue('electorKey');
        const name = form.getFieldValue('name');
        const firstLastName = form.getFieldValue('firstLastName');
        const secondLastName = form.getFieldValue('secondLastName');
        
        if (electorKey && name && firstLastName && secondLastName) {
            const isValid = IneValidator.validateNameMatch(electorKey, name, firstLastName, secondLastName);
            if (!isValid) {
                return Promise.reject('No coincide con la clave INE');
            }
        }
        return Promise.resolve();
    };

    if (!PROMOTER_REGISTRATION_ENABLED) {
        return (
            <>
                {contextHolder}
                <motion.div
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5}}
                    style={{
                        padding: "20px",
                        background: theme.colors.card,
                        borderRadius: "8px",
                        maxWidth: "800px",
                        width: "90vw",
                        margin: "20px auto",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        textAlign: 'center',
                    }}
                >
                    <Title level={2} style={{textAlign: "center", marginBottom: "20px"}}>Registro de Promotor</Title>
                    <Alert
                        message="Registro de promotores cerrado"
                        description="Ya no es posible registrar nuevos promotores."
                        type="warning"
                        showIcon
                    />
                </motion.div>
            </>
        );
    }

    return (
        <>
            {contextHolder}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    padding: "20px",
                    background: theme.colors.card,
                    borderRadius: "8px",
                    maxWidth: "800px",
                    width: "90vw",
                    margin: "20px auto",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    textAlign: 'center'
                }}
            >
                <Title level={2} style={{textAlign: "center", marginBottom: "20px"}}>Registro de Promotor</Title>

                {limitReached ? (
                    <div style={{textAlign: 'center', padding: '40px'}}>
                        <ExclamationCircleOutlined style={{color: '#faad14', fontSize: 48, marginBottom: 16}}/>
                        <Title level={3}>Límite de promotores alcanzado</Title>
                        <Text style={{fontSize: '16px', color: '#666'}}>
                            No está permitido agregar más promotores en este momento.
                        </Text>
                        <div style={{marginTop: 24}}>
                            <Button type="primary" onClick={() => navigate('/promotores')}>
                                Volver a la lista
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        {!connectionStatus && (
                            <Alert
                                message="Modo sin conexión a internet"
                                description="Los registros se guardarán localmente y se sincronizarán automáticamente cuando vuelvas a tener conexión."
                                type="warning"
                                showIcon
                                style={{marginBottom: '16px'}}
                            />
                        )}

                        <motion.div
                            initial={{opacity: 0.5}}
                            animate={{opacity: 1}}
                            transition={{duration: 0.5, repeat: Infinity, repeatType: "reverse"}}
                            style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}
                        >
                            {connectionStatus ? (
                                <>
                                    <CheckCircleOutlined style={{color: '#52c41a', fontSize: '16px'}}/>
                                    <Text strong style={{color: '#52c41a'}}>Conectado a internet</Text>
                                </>
                            ) : (
                                <>
                                    <CloseCircleOutlined style={{color: '#f5222d', fontSize: '16px'}}/>
                                    <Text strong style={{color: '#f5222d'}}>Sin conexión a internet</Text>
                                </>
                            )}
                        </motion.div>

                        <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                    style={{marginTop: "20px", textAlign: "left"}}
                >
                    <Form.Item
                        label="CURP *"
                        name="curp"
                        rules={[
                            {required: true, message: 'El CURP es obligatorio.'},
                            {
                                pattern: /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/,
                                message: 'El CURP debe tener 18 caracteres y formato válido.'
                            },
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('electorKey') !== value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject('El CURP y la Clave de Elector no pueden ser iguales.');
                                }
                            })
                        ]}
                        dependencies={['electorKey']}
                    >
                        <Input placeholder="Ingrese el CURP" maxLength={18} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    <Form.Item
                        label="Clave de Elector *"
                        name="electorKey"
                        rules={[
                            {
                                required: true,
                                validator: (_, value) =>
                                    electorKeyValidator.validate(value) ?
                                        Promise.resolve() :
                                        Promise.reject(new Error('Clave de elector inválida.')),
                            },
                        ]}
                    >
                        <Input placeholder="Ingrese la clave de elector" maxLength={18} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Nombre *"
                                name="name"
                                rules={[
                                    {required: true, message: 'El nombre es obligatorio.'},
                                    {validator: validateIneMatch}
                                ]}
                                dependencies={['electorKey', 'firstLastName', 'secondLastName']}
                            >
                                <Input placeholder="Nombre" onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                                }}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Apellido Paterno *"
                                name="firstLastName"
                                rules={[
                                    {required: true, message: 'El apellido paterno es obligatorio.'},
                                    {validator: validateIneMatch}
                                ]}
                                dependencies={['electorKey', 'name', 'secondLastName']}
                            >
                                <Input placeholder="Apellido Paterno" onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                                }}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item 
                                label={
                                    <span>
                                        Apellido Materno (o XX){' '}
                                        <Tooltip title="En caso de no contar con segundo apellido debe escribir XX">
                                            <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                        </Tooltip>
                                    </span>
                                }
                                name="secondLastName"
                                rules={[
                                    {required: true, message: 'El apellido materno es obligatorio (o XX si no tiene).'},
                                    {validator: validateIneMatch}
                                ]}
                                dependencies={['electorKey', 'name', 'firstLastName']}
                            >
                                <Input placeholder="Apellido Materno" onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                                }}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Correo Electrónico *"
                        name="email"
                        rules={[
                            {required: true, message: 'El correo electrónico es obligatorio.'},
                            {type: 'email', message: 'Debe ser un correo electrónico válido.'},
                            temporaryEmailRule,
                        ]}
                    >
                        <Input placeholder="Correo Electrónico"/>
                    </Form.Item>

                    <Form.Item
                        label="Teléfono (10 dígitos con lada) *"
                        name="phone"
                        rules={[
                            {required: true, message: 'El teléfono es obligatorio.'},
                            {
                                validator: async (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const cleanedValue = value.replace(/\D/g, '');
                                    if (cleanedValue.length !== 10) {
                                        return Promise.reject(new Error('Debe contener exactamente 10 dígitos.'));
                                    }
                                    const lada3 = cleanedValue.substring(0, 3);
                                    const lada2 = cleanedValue.substring(0, 2);
                                    const validLada3 = areaCodes.some(ac => ac.areaCode.toString() === lada3);
                                    const validLada2 = ['33', '55', '56', '81'].includes(lada2) && areaCodes.some(ac => ac.areaCode.toString() === lada2);
                                    
                                    if (!validLada3 && !validLada2) {
                                        return Promise.reject(new Error(`La lada no es válida.`));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input placeholder="5551234567" maxLength={10} onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, '');
                        }}/>
                    </Form.Item>

                    <Form.Item
                        label="Calle *"
                        name="street"
                        rules={[{required: true, message: 'La calle es obligatoria.'}]}
                    >
                        <Input placeholder="Ingrese la calle" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item
                                label="Número Exterior *"
                                name="exteriorNumber"
                                rules={[{required: true, message: 'El número exterior es obligatorio.'}]}
                            >
                                <Input placeholder="Número Exterior" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item
                                label="Número Interior"
                                name="interiorNumber"
                            >
                                <Input placeholder="Número Interior (Opcional)" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 8}>
                            {/* TEMPORAL: Validación de rango de código postal deshabilitada temporalmente */}
                            <Form.Item
                                label="Código Postal *"
                                name="zipCode"
                                rules={[
                                    {required: true, message: 'El código postal es obligatorio.'},
                                    {
                                        pattern: /^\d{5}$/,
                                        message: 'Debe contener exactamente 5 dígitos numéricos.'
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Código Postal"
                                    maxLength={5}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/\D/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Colonia *"
                                name="neighborhood"
                                rules={[{required: true, message: 'Este campo es obligatorio.'}]}
                            >
                                <Input placeholder="Colonia" onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 8}>
                            <Form.Item
                                label="Municipio/Delegación"
                                name="municipalityName"
                                initialValue={section.municipalityName}
                            >
                                <Input placeholder="Municipio/Delegación" disabled={true}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Estado"
                        name="entityName"
                        initialValue={section.entityName}
                    >
                        <Input placeholder="Estado" disabled={true}/>
                    </Form.Item>

                    <Form.Item
                        label="Sección *"
                        name="sectionId"
                        initialValue={userSections.length === 1 ? userSections[0] : null}
                        rules={[{required: true, message: 'Debe seleccionar una sección.'}]}
                    >
                        {userSections.length === 1 ? (
                            <Input value={`Sección ${userSections[0]}`} disabled={true}/>
                        ) : (
                            <Select placeholder="Seleccione una sección">
                                {userSections.map((sec) => (
                                    <Option key={sec} value={sec}>{`Sección ${sec}`}</Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item>
                        <Space style={{width: '100%', justifyContent: 'right'}}>
                            <Button onClick={() => navigate('/promotores')}>
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                style={{
                                    backgroundColor: connectionStatus ? '#52c41a' : '#f5222d',
                                    borderColor: connectionStatus ? '#52c41a' : '#f5222d'
                                }}
                            >
                                {connectionStatus ? "Enviar registro" : "Guardar registro"}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                <Modal
                    open={modalVisible}
                    footer={null}
                    closable={false}
                    centered
                    onCancel={handleModalClose}
                >
                    {modalType === 'success' && (
                        <div style={{textAlign: 'center'}}>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: 48}}/>
                            <Title level={3}>Registro Exitoso</Title>
                            <Text>{modalMessage}</Text>
                            <div style={{marginTop: 16}}>
                                <Button type="primary" onClick={handleModalClose}>
                                    Aceptar
                                </Button>
                            </div>
                        </div>
                    )}
                    {modalType === 'error' && (
                        <div style={{textAlign: 'center'}}>
                            <CloseCircleOutlined style={{color: '#f5222d', fontSize: 48}}/>
                            <Title level={3}>Error</Title>
                            <Text>{modalMessage}</Text>
                            <div style={{marginTop: 16}}>
                                <Button onClick={handleModalClose}>
                                    Cerrar
                                </Button>
                            </div>
                        </div>
                    )}
                    {modalType === 'offline' && (
                        <div style={{textAlign: 'center'}}>
                            <ExclamationCircleOutlined style={{color: '#faad14', fontSize: 48}}/>
                            <Title level={3}>Modo Offline</Title>
                            <Text>{modalMessage}</Text>
                            <div style={{marginTop: 16}}>
                                <Button type="primary" onClick={handleModalClose}>
                                    Aceptar
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal>
                    </div>
                )}
            </motion.div>
        </>
    );
};

export default PromoterRegisterPage;
